import { useState } from 'react';
import { Link, NavLink } from 'react-router-dom';
import { Menu, X, ArrowUpRight } from 'lucide-react';
import { FnkMark } from '@/components/branding';
import { Button } from '@/components/ui';
import { SITE } from '@/lib/site';
import { cn } from '@/lib/utils';

export const menuItems = [
  { path: '/', label: 'Home' },
  { path: '/product', label: 'Product' },
  { path: '/benchmark', label: 'Benchmark' },
  { path: '/pricing', label: 'Pricing' },
  { path: '/vision', label: 'Vision' },
  { path: '/about', label: 'About' },
];

/** Sticky top bar — solid ground, hairline rule, no blur. Collapses to a full-screen menu below md. */
export function TopNav() {
  const [open, setOpen] = useState(false);

  return (
    <>
      <div className="sticky top-0 z-50 border-b border-line bg-paper/95">
        <div className="mx-auto flex h-16 max-w-site items-center justify-between px-4 sm:px-6">
          <Link to="/" className="flex items-center gap-3" onClick={() => setOpen(false)}>
            <FnkMark />
            <span className="text-lg font-semibold tracking-heading">Fnk</span>
            <span className="hidden font-mono text-[11px] uppercase tracking-label text-ink-4 sm:inline">by FNK Labs</span>
          </Link>

          <nav className="hidden items-center gap-7 md:flex">
            {menuItems.map((item) => (
              <NavLink
                key={item.path}
                to={item.path}
                end={item.path === '/'}
                className={({ isActive }) =>
                  cn('link-rule text-sm font-medium transition-colors', isActive ? 'text-ink' : 'text-ink-3 hover:text-ink')
                }
              >
                {item.label}
              </NavLink>
            ))}
          </nav>

          <div className="flex items-center gap-3">
            <a href={SITE.assessmentMailto} className="hidden sm:block">
              <Button variant="primary" size="sm" rightIcon={<ArrowUpRight className="h-3.5 w-3.5" />}>
                Request an assessment
              </Button>
            </a>
            <button
              type="button"
              className="text-ink-2 hover:text-ink md:hidden"
              onClick={() => setOpen((v) => !v)}
              aria-label={open ? 'Close menu' : 'Open menu'}
            >
              {open ? <X /> : <Menu />}
            </button>
          </div>
        </div>
      </div>

      {open && (
        <div className="fixed inset-0 top-16 z-40 flex flex-col bg-paper md:hidden">
          <nav className="flex flex-col border-b border-line">
            {menuItems.map((item) => (
              <NavLink
                key={item.path}
                to={item.path}
                end={item.path === '/'}
                onClick={() => setOpen(false)}
                className={({ isActive }) =>
                  cn(
                    'border-t border-line px-6 py-4 text-lg font-medium',
                    isActive ? 'text-accent-ink' : 'text-ink'
                  )
                }
              >
                {item.label}
              </NavLink>
            ))}
          </nav>
          <div className="px-6 py-6">
            <a href={SITE.assessmentMailto} className="block">
              <Button variant="primary" className="w-full" rightIcon={<ArrowUpRight className="h-4 w-4" />}>
                Request an assessment
              </Button>
            </a>
            <div className="mt-6 flex gap-5">
              <NavLink to="/terms" onClick={() => setOpen(false)} className="text-xs text-ink-3">Terms</NavLink>
              <NavLink to="/privacy" onClick={() => setOpen(false)} className="text-xs text-ink-3">Privacy</NavLink>
              <NavLink to="/investors" onClick={() => setOpen(false)} className="text-xs text-ink-3">For Investors</NavLink>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
